package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.Role
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LDangerIcon
import dev.lingvo.site.ds.LFilesIcon
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LLanguagesIcon
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LModalSize
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.ProjectDirectory
import kotlin.random.Random
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

internal fun dangerImpactText(
    name: String,
    fileCount: Int,
    keyCount: Int,
    localeCount: Int,
): String = trf(
    // **One sentence, not four fragments and two separators.**
    //
    // This was `"Deleting $name permanently removes " + files + ", " + keys + " and " + locales`,
    // which hands a translator the opening clause and three counted phrases and keeps the joining
    // — a comma and the word "and" — in Kotlin where they cannot reach it. Languages do not agree
    // on how a list of three joins, and this is the sentence somebody reads immediately before
    // deleting a project.
    "app.projects.slug.settings.project_danger_zone.deleting_removes",
    "Deleting {project} permanently removes {files}, {keys} and {locales}.",
    "project" to name,
    "files" to fileLabel(fileCount),
    "keys" to keyLabel(keyCount),
    "locales" to localeLabel(localeCount),
)

val ProjectDangerActionStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(100.percent)
            .styleModifier { property("justify-self", "stretch") }
    }
    Breakpoint.XL {
        Modifier
            .maxWidth(14.cssRem)
            .styleModifier { property("justify-self", "end") }
    }
}

@Composable
internal fun ProjectDangerZone(
    project: ProjectDetailDto,
    slug: String,
    role: Role?,
    onDeleted: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
    deleteProject: suspend (String) -> Unit = { projectSlug -> ApiClient.deleteProject(projectSlug) },
    invalidate: () -> Unit = { ProjectDirectory.invalidate() },
) {
    val scope = rememberCoroutineScope()
    var open by remember { mutableStateOf(false) }
    // **Deliberately not `LConfirmDialog`, and this is the one place that is true.**
    //
    // A consistency sweep converted this to the shared component and the end-to-end test caught what
    // it cost: after a recoverable failure the typed slug was gone, so an owner who hit a transient
    // network error had to retype the project name to try again — on the one action in the product
    // that destroys a project. The shared dialog also focuses its confirmation field rather than the
    // button that failed, and announces a generic "Finishing" rather than naming what is running.
    //
    // Two of those three are now fixed in `LConfirmDialog` itself, because they were worth having
    // everywhere: it takes a `busyLabel`, its busy region carries `role="status"`, and it returns
    // focus to the confirm button when a recoverable error arrives. The retained value is the one
    // that is genuinely local — this dialog holds the typed slug at the page, above everything that
    // recomposes when the request fails, and that is why it survives.
    //
    // Convert this only when the shared component keeps a typed confirmation across a failure, and
    // when `ProjectDangerZoneSurfaceTest` still passes afterwards.
    var confirmation by remember { mutableStateOf("") }
    var deleting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val submitId = remember { "project-danger-submit-${Random.nextInt(1_000_000)}" }
    val isOwner = role == Role.OWNER

    LaunchedEffect(open, deleting, error, submitId) {
        if (open && !deleting && error != null) {
            (document.getElementById(submitId) as? HTMLElement)?.focus()
        }
    }

    SettingsSection(
        title = t("app.projects.slug.settings.project_danger_zone.danger_zone", "Danger zone"),
        description = t("app.projects.slug.settings.project_danger_zone.permanent_project_deletion_is_restricted_to", "Permanent project deletion is restricted to workspace owners."),
        wide = true,
        icon = { LDangerIcon() },
    ) {
        if (!isOwner) {
            LAlert(
                title = t("app.projects.slug.settings.project_danger_zone.only_a_workspace_owner_can_delete", "Only a workspace owner can delete this project"),
                description = t("app.projects.slug.settings.project_danger_zone.your_role_can_inspect_project_settings", "Your role can inspect project settings, but deletion removes shared workspace data."),
                variant = LAlertVariant.INFO,
            )
        } else {
            LCard(
                Modifier
                    .fillMaxWidth()
                    // Long unbroken values — a project slug, an id — must wrap rather than widen
                    // the grid cell they sit in. This is what `settingsCardModifier` carried beyond
                    // the card recipe, and the only part of it that was not already `LCardStyle`.
                    .minWidth(0.px)
                    .gap(Space.LG)
                    .styleModifier {
                        property("overflow-wrap", "anywhere")
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 448px), 1fr))")
                        property("align-items", "center")
                    }
                    .attr("data-danger-zone", ""),
            ) {
                Column(
                    Modifier.gap(Space.XS).styleModifier { property("min-width", "0") }
                        .attr("data-danger-copy", ""),
                ) {
                    Span(Modifier.fontSize(TypeScale.Body).fontWeight(700).toAttrs { attr("dir", "auto") }) {
                        Text(tf("app.projects.slug.settings.project_danger_zone.delete_named", "Delete {project}", "project" to project.name))
                    }
                    Span(
                        Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.BodySmall)
                            .styleModifier { property("overflow-wrap", "anywhere") }.toAttrs(),
                    ) {
                        Text(t("app.projects.slug.settings.project_danger_zone.deleting_this_project_removes_all_of", "Deleting this project removes all of its localization data:"))
                    }
                    Div(
                        Modifier
                            .fillMaxWidth()
                            .gap(Space.SM)
                            .styleModifier {
                                property("display", "grid")
                                property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 112px), 1fr))")
                            }
                            .attr("role", "list")
                            .attr("aria-label", t("app.projects.slug.settings.project_danger_zone.project_data_that_will_be_deleted", "Project data that will be deleted"))
                            .attr("data-danger-impact", "")
                            .toAttrs(),
                    ) {
                        DangerImpactItem(fileLabel(project.fileCount)) {
                            LFilesIcon(Modifier.iconSize(IconSize.LG))
                        }
                        DangerImpactItem(keyLabel(project.keyCount)) {
                            LKeyIcon(Modifier.iconSize(IconSize.LG))
                        }
                        DangerImpactItem(localeLabel(project.localeCount)) {
                            LLanguagesIcon(Modifier.iconSize(IconSize.LG))
                        }
                    }
                    Span(Modifier.color(ColorMode.current.toSitePalette().textMuted).fontSize(TypeScale.Small).toAttrs()) {
                        Text(t("app.projects.slug.settings.project_danger_zone.translations_activity_and_history_in_those", "Translations, activity and history in those records cannot be recovered."))
                    }
                }
                Div(
                    ProjectDangerActionStyle.toModifier()
                        .attr("data-danger-action", "")
                        .toAttrs(),
                ) {
                    LButton(
                        label = t("app.projects.slug.settings.project_danger_zone.delete_project", "Delete project"),
                        onClick = { confirmation = ""; error = null; open = true },
                        modifier = Modifier.fillMaxWidth(),
                        variant = LButtonVariant.DANGER,
                        leadingIcon = { LTrashIcon() },
                    )
                }
            }
        }
    }

    LModal(
        open = open,
        title = tf("app.projects.slug.settings.project_danger_zone.delete_permanently_title", "Delete {project} permanently?", "project" to project.name),
        modifier = Modifier
            .attr("data-danger-dialog", "")
            .attr("aria-busy", deleting.toString()),
        size = LModalSize.SM,
        onClose = {
            if (!deleting) {
                open = false
                confirmation = ""
                error = null
            }
        },
        actions = {
            LButton(
                label = t("app.projects.slug.settings.project_danger_zone.cancel", "Cancel"),
                onClick = { open = false; confirmation = ""; error = null },
                variant = LButtonVariant.GHOST,
                enabled = !deleting,
            )
            LButton(
                label = t("app.projects.slug.settings.project_danger_zone.delete_project", "Delete project"),
                onClick = {
                    scope.launch {
                        deleting = true
                        error = null
                        try {
                            deleteProject(slug)
                            invalidate()
                            notify(
                                tr("app.projects.slug.settings.project_danger_zone.project_deleted", "Project deleted"),
                                LToastVariant.SUCCESS,
                                4_000,
                            )
                            open = false
                            onDeleted()
                        } catch (cancelled: CancellationException) {
                            throw cancelled
                        } catch (failure: Throwable) {
                            error = failure.message ?: tr(
                                "app.projects.slug.settings.project_danger_zone.check_your_connection",
                                "Check your connection and retry.",
                            )
                        } finally {
                            deleting = false
                        }
                    }
                },
                variant = LButtonVariant.DANGER,
                modifier = Modifier.attr("id", submitId),
                enabled = confirmation == project.slug && !deleting,
                leadingIcon = { LTrashIcon() },
                loading = deleting,
            )
        },
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            Span(Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.BodySmall).toAttrs()) {
                Text(dangerImpactText(project.name, project.fileCount, project.keyCount, project.localeCount))
            }
            Span(Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.BodySmall).toAttrs()) {
                Text(tf(
                    "app.projects.slug.settings.project_danger_zone.type_slug_to_confirm",
                    "Type {slug} exactly to confirm. Matching is case-sensitive.",
                    "slug" to project.slug,
                ))
            }
            LTextField(
                value = confirmation,
                onValueChange = { confirmation = it; error = null },
                label = t("app.projects.slug.settings.project_danger_zone.project_slug", "Project slug"),
                enabled = !deleting,
            )
            if (deleting) {
                Span(
                    Modifier
                        .color(ColorMode.current.toSitePalette().textSecondary)
                        .fontSize(TypeScale.BodySmall)
                        .attr("role", "status")
                        .attr("aria-live", "polite")
                        .toAttrs(),
                ) { Text(t("app.projects.slug.settings.project_danger_zone.deleting_project", "Deleting project…")) }
            }
            error?.let {
                LAlert(
                    title = tf("app.projects.slug.settings.project_danger_zone.delete_failed_title", "Couldn't delete {project}", "project" to project.name),
                    description = it,
                    variant = LAlertVariant.DANGER,
                    live = true,
                )
            }
        }
    }
}

@Composable
private fun DangerImpactItem(
    label: String,
    icon: @Composable () -> Unit,
) {
    Div(
        Modifier
            .minWidth(0.px)
            .color(ColorMode.current.toSitePalette().textSecondary)
            .fontSize(TypeScale.Small)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("overflow-wrap", "anywhere")
            }
            .attr("role", "listitem")
            .toAttrs(),
    ) {
        icon()
        Span(Modifier.fontWeight(600).toAttrs()) { Text(label) }
    }
}

/**
 * The three nouns this screen counts, each through the catalogue.
 *
 * `"${singular}s"` is English's plural rule reached by string concatenation — it is wrong in most
 * languages and it was doing the counting in the confirmation for the one action here that cannot
 * be undone. `tPlural` asks the browser which form the reader's own language selects.
 */
private fun fileLabel(count: Int): String =
    tPlural("app.projects.slug.settings.project_danger_zone.count.file", count, "{count} file", "{count} files")

private fun keyLabel(count: Int): String =
    tPlural("app.projects.slug.settings.project_danger_zone.count.key", count, "{count} key", "{count} keys")

private fun localeLabel(count: Int): String =
    tPlural("app.projects.slug.settings.project_danger_zone.count.locale", count, "{count} locale", "{count} locales")
